import { useRef, useState, type FormEvent } from "react";
import { api, API, ApiError } from "../api";
import { formatBytes, timeAgo } from "./ui";

export interface Comment {
  id: string;
  authorName: string;
  mine: boolean;
  internal: boolean;
  body: string;
  createdAt: string;
}
export interface Attachment {
  id: string;
  name: string;
  contentType: string;
  size: number;
  createdAt: string;
}

export function Thread({
  itemKey,
  comments,
  attachments,
  staff,
  onChange,
  replyHint,
}: {
  itemKey: number;
  comments: Comment[];
  attachments: Attachment[];
  staff: boolean;
  onChange: () => void;
  replyHint?: string;
}) {
  const [body, setBody] = useState("");
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/items/${itemKey}/comments`, { method: "POST", json: { body, internal } });
      setBody("");
      onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't send. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("file", file);
        await api(`/items/${itemKey}/attachments`, { method: "POST", body: form });
      }
      onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Upload failed.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="stack">
      {attachments.length > 0 && (
        <section className="card card-tight">
          <h3>Files</h3>
          <ul className="list-plain">
            {attachments.map((a) => (
              <li key={a.id} className="row" style={{ justifyContent: "space-between" }}>
                <a href={`${API}/attachments/${a.id}`}>{a.name}</a>
                <span className="small muted">
                  {formatBytes(a.size)} · {timeAgo(a.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section aria-label="Conversation" className="thread">
        {comments.length === 0 && <p className="muted small">No replies yet.</p>}
        {comments.map((c) => (
          <article key={c.id} className={`comment${c.internal ? " comment-internal" : ""}`}>
            <div className="comment-meta">
              <strong>{c.authorName}</strong>
              <span>{timeAgo(c.createdAt)}</span>
              {c.internal && <span className="mono">INTERNAL NOTE</span>}
            </div>
            <div className="prewrap">{c.body}</div>
          </article>
        ))}
      </section>
      <form onSubmit={send} className="card stack" data-tour="reply-box">
        <div className="field">
          <label htmlFor="reply">{staff ? (internal ? "Internal note (staff only)" : "Reply to customer") : "Add a reply"}</label>
          <textarea id="reply" value={body} onChange={(e) => setBody(e.target.value)} maxLength={20000} />
          {replyHint && <span className="hint">{replyHint}</span>}
        </div>
        {error && <p className="error-text" role="alert">{error}</p>}
        <div className="row" style={{ justifyContent: "space-between" }}>
          <div className="row">
            <label className="btn btn-small" style={{ cursor: "pointer" }}>
              Attach files
              <input ref={fileRef} type="file" multiple className="sr-only" onChange={(e) => upload(e.target.files)} />
            </label>
            {staff && (
              <label className="check">
                <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} /> Internal note
              </label>
            )}
          </div>
          <button className="btn btn-primary" disabled={busy || !body.trim()}>
            {busy ? "Sending…" : staff && internal ? "Add note" : "Send reply"}
          </button>
        </div>
      </form>
    </div>
  );
}
